import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

const MAX_ATTEMPTS = 6;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | expired | error
  const [info, setInfo] = useState(null);

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setState("error"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setInfo(data);
        setState("paid");
        return;
      }
      if (data.status === "expired") { setState("expired"); return; }
      if (attempt >= MAX_ATTEMPTS) { setState("error"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch {
      if (attempt >= MAX_ATTEMPTS) { setState("error"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { window.scrollTo(0, 0); poll(0); }, [poll]);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="payment-success-page">
      <Header />
      <section className="relative min-h-[70vh] flex items-center justify-center px-5 py-28">
        <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#0a0a0a] p-8 lg:p-10 text-center">
          {state === "checking" && (
            <div data-testid="pay-checking">
              <Loader2 className="w-12 h-12 mx-auto text-[#EAB308] animate-spin" />
              <h1 className="mt-6 text-2xl font-extrabold text-white">Vérification du paiement…</h1>
              <p className="mt-2 text-white/55">Merci de patienter, nous confirmons votre transaction Stripe.</p>
            </div>
          )}
          {state === "paid" && (
            <div data-testid="pay-success">
              <CheckCircle2 className="w-14 h-14 mx-auto text-green-500" />
              <h1 className="mt-6 text-2xl font-extrabold text-white">Paiement confirmé !</h1>
              <p className="mt-2 text-white/60">
                Vos modules PROGÈRE sont maintenant actifs.
                {info?.amount_total ? ` Montant : ${(info.amount_total / 100).toLocaleString("fr-CA", { minimumFractionDigits: 2 })} ${(info.currency || "cad").toUpperCase()}.` : ""}
              </p>
              <Button
                data-testid="pay-goto-modules"
                onClick={() => navigate("/app/modules")}
                className="mt-7 rounded-lg h-12 px-8 font-semibold bg-[#EAB308] text-black hover:opacity-90 gap-2"
              >
                Accéder à mes modules <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          )}
          {(state === "expired" || state === "error") && (
            <div data-testid="pay-error">
              <XCircle className="w-14 h-14 mx-auto text-[#dc143c]" />
              <h1 className="mt-6 text-2xl font-extrabold text-white">
                {state === "expired" ? "Session de paiement expirée" : "Confirmation en attente"}
              </h1>
              <p className="mt-2 text-white/60">
                {state === "expired"
                  ? "Votre session Stripe a expiré. Vous pouvez recommencer votre sélection."
                  : "Nous n'avons pas encore confirmé le paiement. Si le montant a été débité, vos accès s'activeront automatiquement sous peu."}
              </p>
              <Button
                data-testid="pay-retry"
                onClick={() => navigate("/tarification")}
                variant="outline"
                className="mt-7 rounded-lg h-12 px-8 font-semibold border-white/20 bg-white/5 text-white hover:bg-white/10"
              >
                Retour à la tarification
              </Button>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
